import React from 'react'
import { MDBContainer, MDBRow, MDBCol, MDBBtn, MDBIcon, MDBCollapse, MDBSwitch } from 'mdb-react-ui-kit'
import DashboardLayout from '../../components/Admin/DashboardLayout'
import PageHeading from './../../components/shared/PageHeading.component'
import MediaUploaderButton from './../../components/modals/MediaUploaderModal/button.component'
import AdminApi from './../../api/admin-api/admin-api'
import { getSubsiteLogo } from './../../shared/utilities/getSubsiteLogo.function'
import { normalizeBrandColor, isValidBrandColor } from './../../shared/utilities/brandColor.function'

import './WebsiteBranding.page.scss'

const COLOR_FIELDS = [
  {
    key: 's_brand_bg_color',
    label: 'Background Color',
    help: 'Used for the hero, colored sections and footer.',
    fallback: '#2a4685',
  },
  {
    key: 's_brand_accent_color',
    label: 'Accent Color',
    help: 'Used for buttons and highlights.',
    fallback: '#e60a00',
  },
  {
    key: 's_brand_menu_color',
    label: 'Menu Color',
    help: 'Used for the slide-out mobile menu.',
    fallback: '#2a4685',
  },
  {
    key: 's_brand_text_color',
    label: 'Text Color',
    help: 'Used for section headings and body copy.',
    fallback: '#2d2e32',
  },
]

const HERO_INTRO_MAX = 2000

const Section = ({ title, description, children }) => (
  <section className='branding-section'>
    <h5 className='branding-section-title'>{title}</h5>
    {description ? <p className='branding-section-desc'>{description}</p> : null}
    {children}
  </section>
)

class WebsiteBrandingPage extends React.Component {
  state = {
    isLoading: true,
    isSaving: false,
    isLogoLoaded: false,
    error: null,
    success: null,
    form: {
      s_branded: 0,
      s_logo: '',
      s_brand_bg_color: '',
      s_brand_accent_color: '',
      s_brand_text_color: '',
      s_brand_menu_color: '',
      s_hero_headline: '',
      s_hero_intro: '',
    },
  }

  componentDidMount() {
    this.fetchSiteDetails()
  }

  fetchSiteDetails = patch =>
    AdminApi.getUserSiteDetails()
      .then(result => {
        const data = result?.data?.data
        if (!data) {
          this.setState({ isLoading: false, error: 'Website not found.' })
          return
        }

        this.setState(prev => ({
          isLoading: false,
          isLogoLoaded: false,
          form: {
            s_branded: `${data.s_branded}` === '1' ? 1 : 0,
            s_logo: data.s_logo || '',
            s_brand_bg_color: normalizeBrandColor(data.s_brand_bg_color),
            s_brand_accent_color: normalizeBrandColor(data.s_brand_accent_color),
            s_brand_text_color: normalizeBrandColor(data.s_brand_text_color),
            s_brand_menu_color: normalizeBrandColor(data.s_brand_menu_color),
            s_hero_headline: data.s_hero_headline || '',
            s_hero_intro: data.s_hero_intro || '',
            ...(patch || {}),
          },
        }))
      })
      .catch(() =>
        this.setState({
          isLoading: false,
          error: 'Unable to load your website settings.',
        })
      )

  setField = (key, value) =>
    this.setState(prev => ({
      form: { ...prev.form, [key]: value },
      error: null,
      success: null,
    }))

  setLogo = filepath =>
    this.setState(prev => ({
      form: { ...prev.form, s_logo: filepath },
      isLogoLoaded: false,
      error: null,
    }))

  onLogoUploaded = filepath => {
    if (filepath) {
      this.setLogo(filepath)
      this.setState({ success: 'Logo uploaded.' })
      return
    }

    this.setState({ success: 'Logo uploaded.' })
    this.fetchSiteDetails()
  }

  validate = () => {
    const { form } = this.state

    for (const field of COLOR_FIELDS) {
      const value = form[field.key]
      if (value && !isValidBrandColor(value)) return `${field.label} is not a valid color.`
    }

    if (`${form.s_hero_headline}`.length > 255) return 'Please keep the hero headline under 255 characters.'

    if (`${form.s_hero_intro}`.length > HERO_INTRO_MAX)
      return `Please keep the hero introduction under ${HERO_INTRO_MAX} characters.`

    return null
  }

  save = () => {
    const validationError = this.validate()
    if (validationError) {
      this.setState({ error: validationError, success: null })
      return
    }

    if (this.state.isSaving) return

    const { form } = this.state
    this.setState({ isSaving: true, error: null, success: null })

    AdminApi.updateWebsiteBranding({
      s_branded: form.s_branded,
      s_logo: form.s_logo,
      s_brand_bg_color: form.s_brand_bg_color,
      s_brand_accent_color: form.s_brand_accent_color,
      s_brand_text_color: form.s_brand_text_color,
      s_brand_menu_color: form.s_brand_menu_color,
      s_hero_headline: `${form.s_hero_headline}`.trim(),
      s_hero_intro: `${form.s_hero_intro}`.trim(),
    })
      .then(() => this.setState({ isSaving: false, success: 'Website branding saved.' }))
      .catch(e =>
        this.setState({
          isSaving: false,
          error: e?.response?.data?.msg || 'Unable to save your website branding. Please try again.',
        })
      )
  }

  renderColorField = field => {
    const value = this.state.form[field.key]

    return (
      <MDBCol md='6' lg='3' key={field.key} className='mb-4'>
        <label className='branding-color-label' htmlFor={field.key}>
          {field.label}
        </label>
        <p className='branding-color-help'>{field.help}</p>
        <div className='branding-color-row'>
          <input
            type='color'
            className='branding-color-input'
            id={field.key}
            value={value || field.fallback}
            onChange={e => this.setField(field.key, `${e.target.value}`.toLowerCase())}
          />
          {value ? (
            <button type='button' className='branding-reset' onClick={() => this.setField(field.key, '')}>
              Reset
            </button>
          ) : (
            <span className='branding-color-default'>Default</span>
          )}
        </div>
      </MDBCol>
    )
  }

  renderLogoPreview = logoSrc => {
    const { isLogoLoaded } = this.state

    return (
      <div className={`branding-logo-frame ${logoSrc && isLogoLoaded ? 'is-loaded' : 'is-loading'}`}>
        {logoSrc ? (
          <img
            key={logoSrc}
            alt='Website logo'
            src={logoSrc}
            className='branding-logo-preview'
            onLoad={() => this.setState({ isLogoLoaded: true })}
            onError={() => this.setState({ isLogoLoaded: true })}
          />
        ) : (
          <span className='branding-logo-empty'>
            No logo uploaded — your subsite shows the USA Benefits Group logo.
          </span>
        )}
      </div>
    )
  }

  render() {
    const { form, isLoading, isSaving, error, success } = this.state
    const logoSrc = getSubsiteLogo(form.s_logo)
    const isOn = `${form.s_branded}` === '1'
    const hasBranding = !!(
      form.s_logo ||
      form.s_brand_bg_color ||
      form.s_brand_accent_color ||
      form.s_brand_text_color ||
      form.s_brand_menu_color
    )

    return (
      <DashboardLayout>
        <main id='WebsiteBrandingPage' className='mainSection pb-5'>
          <MDBContainer fluid className='mt-5'>
            <PageHeading
              label='Website Branding'
              url={this.props?.match?.url}
              from={this.props?.location?.state?.from}
            />

            <p className='mt-3'>
              Add your own logo, colors and hero copy to your agent website. Anything you leave blank keeps its USA
              Benefits Group default, and all licensing and disclaimer text stays in place.
            </p>

            {isLoading ? (
              <p>Loading...</p>
            ) : (
              <>
                <Section title='Co-Branding'>
                  <div className='branding-switch'>
                    <MDBSwitch
                      id='s_branded'
                      label={
                        isOn
                          ? 'Enabled: co-branding for my subsite is turned on.'
                          : 'Enable co-branding for my subsite.'
                      }
                      checked={isOn}
                      onChange={() => this.setField('s_branded', isOn ? 0 : 1)}
                    />
                  </div>

                  {isOn && !logoSrc ? (
                    <div className='alert alert-info mb-0 mt-3'>
                      Co-branding is on. Upload a logo below to replace the USA Benefits Group logo — until then it
                      keeps showing.
                    </div>
                  ) : null}
                  {!isOn && hasBranding ? (
                    <div className='alert alert-warning mb-0 mt-3'>
                      Your logo and colors are saved but are not live yet. Turn on co-branding above to apply them. Hero
                      copy applies either way.
                    </div>
                  ) : null}
                </Section>

                <Section
                  title='Your Logo'
                  description='A PNG works best. It replaces the USA Benefits Group logo in your subsite header, mobile menu and footer.'
                >
                  <MDBRow>
                    <MDBCol md='6' lg='5'>
                      {this.renderLogoPreview(logoSrc)}
                      <MediaUploaderButton
                        block
                        size='sm'
                        type='button'
                        label={
                          <>
                            {form.s_logo ? 'Replace' : 'Upload'} Website Logo &nbsp;
                            <MDBIcon icon='cloud-upload-alt' />
                          </>
                        }
                        modalConfig={{
                          uploadType: 'agent_subsite_logo',
                          modalContentTop: (
                            <div className='text--center fw--500'>
                              <h6 className='fw--500'>Upload your website logo (PNG).</h6>
                            </div>
                          ),
                          validationRules: { contentType: ['image/png'] },
                        }}
                        onSuccess={this.onLogoUploaded}
                      />
                      {form.s_logo ? (
                        <button type='button' className='branding-reset d-block mt-2' onClick={() => this.setLogo('')}>
                          Remove logo
                        </button>
                      ) : null}
                    </MDBCol>
                  </MDBRow>
                </Section>

                <Section
                  title='Your Colors'
                  description='Pick a color to replace each USA Benefits Group default. Text is adjusted automatically so it stays readable on whatever you choose.'
                >
                  <MDBRow>{COLOR_FIELDS.map(this.renderColorField)}</MDBRow>
                </Section>

                <Section
                  title='Your Hero Copy'
                  description='The headline and introduction at the top of your subsite home page.'
                >
                  <MDBRow>
                    <MDBCol md='12' className='form-group'>
                      <label htmlFor='s_hero_headline'>Hero Headline</label>
                      <input
                        type='text'
                        className='form-control'
                        id='s_hero_headline'
                        maxLength={255}
                        placeholder='Leave blank to keep the generated headline'
                        value={form.s_hero_headline}
                        onChange={e => this.setField('s_hero_headline', e.target.value)}
                      />
                    </MDBCol>
                    <MDBCol md='12' className='form-group mb-0'>
                      <label htmlFor='s_hero_intro'>Hero Introduction</label>
                      <textarea
                        className='form-control'
                        id='s_hero_intro'
                        rows='4'
                        maxLength={HERO_INTRO_MAX}
                        placeholder='Leave blank to keep the standard introduction'
                        value={form.s_hero_intro}
                        onChange={e => this.setField('s_hero_intro', e.target.value)}
                      />
                    </MDBCol>
                  </MDBRow>
                </Section>

                <MDBRow>
                  <MDBCol md='12'>
                    <MDBBtn color='indigo' type='button' onClick={this.save}>
                      {isSaving ? 'Saving...' : 'Save'}
                    </MDBBtn>
                  </MDBCol>
                </MDBRow>

                <MDBRow>
                  <MDBCol md='12'>
                    <MDBCollapse open={!!error}>
                      <div className='alert alert-danger'>{error}</div>
                    </MDBCollapse>
                    <MDBCollapse open={!!success}>
                      <div className='alert alert-success'>{success}</div>
                    </MDBCollapse>
                  </MDBCol>
                </MDBRow>
              </>
            )}
          </MDBContainer>
        </main>
      </DashboardLayout>
    )
  }
}

export default WebsiteBrandingPage
